import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaChevronRight,
    FaSearch,
    FaPrint,
    FaFilePdf
} from 'react-icons/fa';

const EngineerSubheLotteryList = () => {
    const navigate = useNavigate();
    const [lotteryDate, setLotteryDate] = useState('');
    const [department, setDepartment] = useState('');
    const [taluka, setTaluka] = useState('');
    const [lotteryDates, setLotteryDates] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appliedFilters, setAppliedFilters] = useState({ date: '', dept: '', taluka: '' });

    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    const talukaList = [
        { en: 'Shirala', mr: 'शिराळा' },
        { en: 'Walwa', mr: 'वाळवा' },
        { en: 'Tasgaon', mr: 'तासगाव' },
        { en: 'Khanapur', mr: 'खानापूर (विटा)' },
        { en: 'Atpadi', mr: 'आटपाडी' },
        { en: 'Kavathe Mahankal', mr: 'कवठे महांकाळ' },
        { en: 'Miraj', mr: 'मिरज' },
        { en: 'Palus', mr: 'पलूस' },
        { en: 'Jat', mr: 'जत' },
        { en: 'Kadegaon', mr: 'कडेगाव' },
        { en: 'Mukhyalaya', mr: 'मुख्यालय' }
    ];

    const talukas = [
        "मिरज", "जत", "खानापूर (विटा)", "वाळवा (इस्लामपूर)", "तासगाव", 
        "शिराळा", "कडेगाव", "पलूस", "कवठे महांकाळ"
    ];

    const departments = ["बांधकाम विभाग", "पाणी पुरवठा विभाग", "लघुसिंचन विभाग"];

    useEffect(() => {
        setLoading(true);

        // Fetch Lottery Dates
        const genWorkRef = ref(db, 'ekamwatapzp/generatework');
        const unsubDates = onValue(genWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const dates = Object.keys(snapshot.val());
                setLotteryDates(dates.reverse());
            }
        });

        // Fetch Registrations (Only Subhe for this page)
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubEng = onValue(engRef, (snap) => {
            if (snap.exists()) {
                setRegistrations(Object.entries(snap.val()).map(([key, val]) => ({ ...val, _key: key })));
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubDates();
            unsubEng();
        };
    }, []);

    const normalizeTaluka = (name) => {
        if (!name) return "";
        const n = name.toString().toLowerCase().trim();
        const standardMap = {
            'shirala': 'shirala', 'शिराळा': 'shirala',
            'walwa': 'walwa', 'वाळवा': 'walwa', 'islampur': 'walwa',
            'tasgaon': 'tasgaon', 'तासगाव': 'tasgaon',
            'khanapur': 'khanapur', 'खानापूर': 'khanapur', 'vita': 'khanapur', 'खानापूर (विटा)': 'khanapur',
            'atpadi': 'atpadi', 'आटपाडी': 'atpadi',
            'kavathe mahankal': 'kavathe mahankal', 'कवठे महांकाळ': 'kavathe mahankal', 'kavathemahangal': 'kavathe mahankal', 'कवठेमहांकाळ': 'kavathe mahankal',
            'miraj': 'miraj', 'मिरज': 'miraj', 'sangli': 'miraj',
            'palus': 'palus', 'पलूस': 'palus',
            'jat': 'jat', 'जत': 'jat', 'jath': 'jat',
            'kadegaon': 'kadegaon', 'कडेगाव': 'kadegaon',
            'mukhyalaya': 'mukhyalaya', 'मुख्यालय': 'mukhyalaya'
        };
        return standardMap[n] || n;
    };

    const getMarathiTaluka = (enName) => {
        if (!enName) return "-";
        const normalizedEn = normalizeTaluka(enName);
        const found = talukaList.find(t => normalizeTaluka(t.en) === normalizedEn);
        return found ? found.mr : enName;
    };

    const handleSearch = () => {
        setAppliedFilters({ date: lotteryDate, dept: department, taluka: taluka });
    };

    const displayData = useMemo(() => {
        if (!appliedFilters.date) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedFilters.date]) {
                const lotteryData = reg.GeneratedLottery[appliedFilters.date];
                
                // Department filter (if work has category/department info)
                const deptMatch = !appliedFilters.dept || (lotteryData.department === appliedFilters.dept || lotteryData.schemeName?.includes(appliedFilters.dept));
                
                // Taluka filter
                const standardizedApplied = normalizeTaluka(appliedFilters.taluka);
                const standardizedReg = normalizeTaluka(reg.taluka);
                const talukaMatch = !appliedFilters.taluka || standardizedReg === standardizedApplied;
                
                if (deptMatch && talukaMatch) {
                    results.push({
                        ...lotteryData,
                        id: reg._key,
                        applicantName: reg.name || `${reg.firstName || ''} ${reg.middleName || ''} ${reg.surname || ''}`.trim() || reg.fullName || "-",
                        phoneNumber: reg.whatsappNo || reg.phone || reg.mobile || "-",
                        taluka: reg.taluka || "-"
                    });
                }
            }
        });
        return results;
    }, [registrations, appliedFilters]);

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-10 px-2 text-left">
                    <div className="flex items-center gap-6">
                        <button 
                            onClick={() => navigate('/engineer/lottery-reports-menu')}
                            className="p-4 bg-white rounded-2xl shadow-sm hover:bg-gray-50 transition-all text-gray-600 border border-gray-100 hover:scale-105 active:scale-95 group"
                        >
                            <FaArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                        </button>
                    </div>
                    <div className="hidden md:flex items-center gap-3 text-[11px] font-black text-gray-400 bg-white px-6 py-3 rounded-2xl border border-gray-100 shadow-sm uppercase tracking-wider">
                        जिल्हा परिषद <FaChevronRight size={10} className="text-gray-200" /> <span className="text-purple-600">सुभे सोडतीची लॉटरी यादी</span>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                        <div className="space-y-2 text-left">
                            <label className="text-[13px] font-black text-gray-500 tracking-tight">लॉटरीची तारीख निवडा <span className="text-red-500">*</span></label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-purple-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={lotteryDate}
                                onChange={(e) => setLotteryDate(e.target.value)}
                            >
                                <option value="">लॉटरीची तारीख निवडा</option>
                                {lotteryDates.map(date => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2 text-left">
                            <label className="text-[13px] font-black text-gray-500 tracking-tight">विभाग <span className="text-red-500">*</span></label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-purple-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                            >
                                <option value="">विभाग प्रकार निवडा</option>
                                {departments.map((d, idx) => (
                                    <option key={idx} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2 text-left">
                            <label className="text-[13px] font-black text-gray-500 tracking-tight">तालुका निवडा</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-purple-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={taluka}
                                onChange={(e) => setTaluka(e.target.value)}
                            >
                                <option value="">तालुका निवडा</option>
                                {talukas.map((t, idx) => (
                                    <option key={idx} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <button 
                                onClick={handleSearch}
                                className="bg-[#6366f1] hover:bg-[#4f46e5] text-white px-10 py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition transform active:scale-95 flex items-center justify-center gap-2 w-full md:w-auto"
                            >
                                <FaSearch size={14} />
                                शोध
                            </button>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="mt-10 overflow-hidden border border-gray-100 rounded-[0.5rem] shadow-sm">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-white border-b border-gray-200 text-[13px] font-black text-gray-700 text-left">
                                    <th className="px-8 py-5 border-r border-gray-100 w-24">#</th>
                                    <th className="px-8 py-5 border-r border-gray-100 min-w-[200px]">कामाचे नाव</th>
                                    <th className="px-8 py-5 border-r border-gray-100 min-w-[150px]">रक्कम</th>
                                    <th className="px-8 py-5 border-r border-gray-100 min-w-[250px]">अर्जदाराचे नाव</th>
                                    <th className="px-8 py-5 min-w-[200px]">फोन नंबर.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4 text-gray-300 font-bold">
                                                <div className="animate-spin text-indigo-500 text-2xl">
                                                    <FaSearch size={24} />
                                                </div>
                                                <p className="italic">माहिती लोड होत आहे...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayData.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-10 text-center text-gray-600 font-black bg-[#f8fafc]/50 text-lg">
                                            कोणताही अर्ज आढळला नाही.
                                        </td>
                                    </tr>
                                ) : (
                                    displayData.map((item, index) => (
                                        <tr key={index} className="hover:bg-blue-50/20 transition-colors border-b border-gray-100 last:border-0 text-[14px] text-gray-700 font-bold">
                                            <td className="px-8 py-6 border-r border-gray-100 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                            <td className="px-8 py-6 border-r border-gray-100 flex flex-col gap-1">
                                                <span>{item.workName}</span>
                                                <span className="text-[10px] text-gray-400 font-normal">{getMarathiTaluka(item.taluka)} | {item.schemeName}</span>
                                            </td>
                                            <td className="px-8 py-6 border-r border-gray-100 font-marathi">{toMarathiNumber(item.tenderAmount || item.amount || "-")}</td>
                                            <td className="px-8 py-6 border-r border-gray-100">{item.applicantName}</td>
                                            <td className="px-8 py-6 font-marathi">{toMarathiNumber(item.phoneNumber)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Print Button */}
                    <div className="mt-12 flex justify-center">
                        <button 
                            className="bg-[#6366f1] hover:bg-[#4f46e5] text-white px-10 py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition transform active:scale-95 flex items-center gap-2"
                        >
                            प्रिंट
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheLotteryList;
